// NUMA screen help (the "?" at the top of NUMA). Plain words; keep it in step with
// apps/mobile/src/tools/NumaTool.tsx (river, dials, BALANCE, Charge/Load/Flow boxes).
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const numaHelp: ScreenHelpContent = {
  title: 'NUMA',
  intro:
    "NUMA shows how much you are carrying and whether you can carry it. It reads your open tasks and your day, and gives you three dials: CHARGE, LOAD and FLOW. When a dial is out of the green, NUMA can help you move things until it is back.",
  sections: [
    {
      heading: 'The FLO River',
      body: [
        'Every open task is a ball in the river. Switch the view with Weight or Sector at the top right of the river.',
        'Weight view: the columns are days, starting with Today. INTAKE on the left holds tasks with no date. LATER on the right holds tasks further out. Sector view sorts the balls into your five areas instead.',
        'A bigger ball with a bigger number is a heavier task. The colour shows how long it has sat open: black when new, then darker blue, purple and wine as it ages. Red means overdue, and it sinks the later it gets.',
        'Overdue three days or more, a task becomes a stone and rests on the riverbed.',
      ],
    },
    {
      heading: 'Tap a ball',
      body: ['Tapping a ball opens a card with its list, sector, date, priority, state and weight. From there:'],
      items: [
        { term: 'Mark done', text: 'finishes the task.' },
        { term: 'Reschedule', text: 'pick a day on the calendar and a time (9:00, 12:00, 18:00, Keep time or No time), then Save.' },
        { term: 'Open in KYRS', text: 'jumps to that task in KYRS.' },
        { term: 'Cancel', text: 'closes the card.' },
        { term: 'What are you doing about it?', text: 'shown for stones only. Write one line (reschedule, redistribute, or act) and press Save line (NUMA rep). It is added, with the date, to the task\'s notes.' },
      ],
    },
    {
      heading: 'The three dials',
      body: ['Press and hold a dial to read what it means. Each one is green when it is inside your green band (set in Settings, Game).'],
      items: [
        { term: 'CHARGE', text: 'your energy for today, 0 to 10. It starts full at your wake time and drains with each planned block, the weight of what is still open, and every switch between kinds of work. Breaks of 15 minutes or more give some back. Green at or above your Charge minimum.' },
        { term: 'LOAD', text: 'the total weight of everything open in the river. Green at or below your baseline times your factor. Past that you are carrying more than you can move.' },
        { term: 'FLOW', text: 'how steadily work moves through the river over the last 28 days. Green at or above your Flow minimum.' },
      ],
    },
    {
      heading: 'Get back to green',
      body: [
        'Under the dials, the big number is your Load, with a chip saying "Green band" or "Over green band".',
        'If a dial is out of the green you will see BALANCE. Press it and KYRS and NUMA talk it through out loud and change your tasks for you (giving them a day or setting them aside). They never mark anything done, because only you can say it is finished. "History & changes" shows what they changed.',
        'Prefer to do it yourself? Press "See the moves myself" for a list. Each move shows the weight before and after. Apply does one, "Apply all" does the lot, and KYRS opens the task. For FLOW it suggests a few small tasks to finish today. When all three dials are green it says so.',
      ],
    },
    {
      heading: 'The three boxes below',
      body: ['Each box has tabs at its top right.'],
      items: [
        { term: 'Charge', text: 'Today shows your charge number, whether it is green, and about how long until it reaches 8.0. Protocols shows successes, fails and pauses for the month. Journal shows pie charts for your journal dropdown questions that month. Use the arrows to change month.' },
        { term: 'Load', text: 'By sector splits your load across the five areas. Heaviest ranks your heaviest open tasks. Over time shows total priority load as a Line, Pie or Bar chart for This Week, This Month or This Year.' },
        { term: 'Flow', text: 'Report shows your Flow score and four bars: Cycle, Exchange, Steadiness and Latency. Over time draws your Flow for the last 28 days.' },
      ],
    },
    {
      heading: 'What saves',
      body: [
        'Looking and switching tabs changes nothing. Mark done, Reschedule, Save line, Apply and BALANCE change your tasks right away, and the river refreshes.',
      ],
    },
  ],
};
